import * as React from "react";
import { Form, Badge } from "react-bootstrap";
import { gettext } from "i18n";
import "./SearchableDropdown.scss";

export interface DropdownOption {
  value: string;
  label: string;
  sublabel?: string;
  warning?: boolean;
}

interface SearchableDropdownProps {
  placeholder: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  useFixedPositioning?: boolean;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  placeholder,
  value,
  options,
  onChange,
  disabled = false,
  useFixedPositioning = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [panelPosition, setPanelPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.sublabel?.toLowerCase().includes(q),
    );
  }, [query, options]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  React.useEffect(() => {
    if (open && useFixedPositioning && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, useFixedPositioning]);

  return (
    <div
      className={`SearchableDropdown${disabled ? " SearchableDropdown--disabled" : ""}${open ? " SearchableDropdown--open" : ""}`}
      ref={containerRef}
    >
      <button
        type="button"
        className="SearchableDropdown__trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span
          className={
            selectedOption
              ? "SearchableDropdown__selected"
              : "SearchableDropdown__placeholder"
          }
        >
          {selectedOption ? (
            <>
              {selectedOption.label}
              {selectedOption.sublabel && (
                <span className="SearchableDropdown__sublabel">
                  {" "}
                  - {selectedOption.sublabel}
                </span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <span className="SearchableDropdown__chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          className="SearchableDropdown__panel"
          style={
            useFixedPositioning
              ? {
                  position: "fixed",
                  top: panelPosition.top,
                  left: panelPosition.left,
                  width: panelPosition.width,
                }
              : undefined
          }
        >
          <div className="SearchableDropdown__search-wrap">
            <Form.Control
              type="text"
              autoFocus
              placeholder={gettext("Search ...")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="SearchableDropdown__search"
            />
          </div>
          <ul className="SearchableDropdown__list">
            {filtered.length === 0 ? (
              <li className="SearchableDropdown__empty">
                {gettext("No results")}
              </li>
            ) : (
              filtered.map((o) => (
                <li
                  key={o.value}
                  className={`SearchableDropdown__option${o.value === value ? " SearchableDropdown__option--selected" : ""}`}
                  onClick={() => handleSelect(o.value)}
                >
                  {o.warning && (
                    <Badge bg="warning" className="me-2">
                      ⚠️
                    </Badge>
                  )}
                  <div className="SearchableDropdown__option-text">
                    <span className="SearchableDropdown__option-label">
                      {o.label}
                    </span>
                    {o.sublabel && (
                      <span className="SearchableDropdown__option-sublabel">
                        {o.sublabel}
                      </span>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

interface SearchableCheckboxDropdownProps {
  placeholder: string;
  values: string[];
  options: DropdownOption[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  useFixedPositioning?: boolean;
  summaryFormatter?: (count: number, labels: string[]) => string;
}

export const SearchableCheckboxDropdown: React.FC<
  SearchableCheckboxDropdownProps
> = ({
  placeholder,
  values,
  options,
  onChange,
  disabled = false,
  useFixedPositioning = false,
  summaryFormatter,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [panelPosition, setPanelPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.sublabel?.toLowerCase().includes(q),
    );
  }, [query, options]);

  const selectedLabels = React.useMemo(() => {
    return values
      .map((val) => options.find((o) => o.value === val)?.label)
      .filter(Boolean) as string[];
  }, [values, options]);

  const displayText = React.useMemo(() => {
    if (values.length === 0) {
      return placeholder;
    }
    if (summaryFormatter) {
      return summaryFormatter(values.length, selectedLabels);
    }
    if (values.length === 1) {
      return selectedLabels[0];
    }
    return `${values.length} selected`;
  }, [values.length, selectedLabels, placeholder, summaryFormatter]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleValue = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  React.useEffect(() => {
    if (open && useFixedPositioning && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, useFixedPositioning]);

  return (
    <div
      className={`SearchableDropdown${disabled ? " SearchableDropdown--disabled" : ""}${open ? " SearchableDropdown--open" : ""}`}
      ref={containerRef}
    >
      <button
        type="button"
        className="SearchableDropdown__trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span
          className={
            values.length > 0
              ? "SearchableDropdown__selected"
              : "SearchableDropdown__placeholder"
          }
        >
          {displayText}
        </span>
        <span className="SearchableDropdown__chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          className="SearchableDropdown__panel"
          style={
            useFixedPositioning
              ? {
                  position: "fixed",
                  top: panelPosition.top,
                  left: panelPosition.left,
                  width: panelPosition.width,
                }
              : undefined
          }
        >
          <div className="SearchableDropdown__search-wrap">
            <Form.Control
              type="text"
              autoFocus
              placeholder={gettext("Search ...")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="SearchableDropdown__search"
            />
          </div>
          <ul className="SearchableDropdown__list">
            {filtered.length === 0 ? (
              <li className="SearchableDropdown__empty">
                {gettext("No results")}
              </li>
            ) : (
              filtered.map((o) => (
                <li
                  key={o.value}
                  className={`SearchableDropdown__option SearchableDropdown__option--checkbox${values.includes(o.value) ? " SearchableDropdown__option--selected" : ""}`}
                  onClick={() => toggleValue(o.value)}
                >
                  <input
                    type="checkbox"
                    checked={values.includes(o.value)}
                    onChange={() => {}}
                    className="SearchableDropdown__checkbox"
                  />
                  <div className="SearchableDropdown__option-text">
                    <span className="SearchableDropdown__option-label">
                      {o.label}
                    </span>
                    {o.sublabel && (
                      <span className="SearchableDropdown__option-sublabel">
                        {o.sublabel}
                      </span>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

interface SearchableMultiDropdownProps {
  placeholder: string;
  values: string[];
  options: DropdownOption[];
  onChange: (values: string[]) => void;
  useFixedPositioning?: boolean;
}

export const SearchableMultiDropdown: React.FC<
  SearchableMultiDropdownProps
> = ({
  placeholder,
  values,
  options,
  onChange,
  useFixedPositioning = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [panelPosition, setPanelPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(query.toLowerCase()),
    );
  }, [query, options]);

  const valueCounts = React.useMemo(() => {
    return values.reduce(
      (acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [values]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addValue = (val: string) => {
    onChange([...values, val]);
  };

  React.useEffect(() => {
    if (open && useFixedPositioning && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, useFixedPositioning]);

  const uniqueSelected = Array.from(new Set(values));
  const selectedLabels = uniqueSelected
    .map((val) => {
      const option = options.find((o) => o.value === val);
      return option
        ? { label: option.label, count: valueCounts[val] || 1 }
        : null;
    })
    .filter(Boolean) as { label: string; count: number }[];

  return (
    <div
      className={`SearchableDropdown${open ? " SearchableDropdown--open" : ""}`}
      ref={containerRef}
    >
      <button
        type="button"
        className="SearchableDropdown__trigger SearchableDropdown__trigger--multi"
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={
            values.length > 0
              ? "SearchableDropdown__selected"
              : "SearchableDropdown__placeholder"
          }
        >
          {values.length > 0 ? (
            <span className="SearchableDropdown__tags">
              {selectedLabels.map(({ label, count }) => (
                <Badge
                  key={label}
                  bg="secondary"
                  className="SearchableDropdown__tag"
                >
                  {label}
                  {count > 1 ? ` (${count})` : ""}
                </Badge>
              ))}
            </span>
          ) : (
            placeholder
          )}
        </span>
        <span className="SearchableDropdown__chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          className="SearchableDropdown__panel"
          style={
            useFixedPositioning
              ? {
                  position: "fixed",
                  top: panelPosition.top,
                  left: panelPosition.left,
                  width: panelPosition.width,
                }
              : undefined
          }
        >
          <div className="SearchableDropdown__search-wrap">
            <Form.Control
              type="text"
              autoFocus
              placeholder={gettext("Search ...")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="SearchableDropdown__search"
            />
          </div>
          <ul className="SearchableDropdown__list">
            {filtered.length === 0 ? (
              <li className="SearchableDropdown__empty">
                {gettext("No results")}
              </li>
            ) : (
              filtered.map((o) => (
                <li
                  key={o.value}
                  className={`SearchableDropdown__option${values.includes(o.value) ? " SearchableDropdown__option--selected" : ""}`}
                  onClick={() => addValue(o.value)}
                >
                  <span className="SearchableDropdown__option-check">
                    {values.includes(o.value) && "✓"}
                  </span>
                  <span className="SearchableDropdown__option-label">
                    {o.label}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
